import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Repeat,
  Delete,
  Edit,
  Add,
  Schedule,
  NotificationsActive,
  CalendarToday
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import RecurringTaskForm from './RecurringTaskForm';

interface RecurringTask {
  id: string;
  title: string;
  description: string;
  scheduled_hour: number;
  label: string;
  priority: number;
  recurrence_type: string;
  recurrence_days: number[] | null;
  recurrence_day_of_month: number | null;
  recurrence_interval: number;
  reminder_enabled: boolean;
  reminder_minutes_before: number;
  is_active: boolean;
  patternDescription: string;
}

const DAYS_MAP: { [key: number]: string } = {
  1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun'
};

const formatTime = (hour: number) => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:00 ${period}`;
};

const RecurringTasksList: React.FC = () => {
  const [tasks, setTasks] = useState<RecurringTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await apiService.getRecurringTasks();
      if (response.success) {
        setTasks(response.recurringTasks);
      }
    } catch (err: any) {
      setError('Failed to load recurring tasks');
      console.error('Load recurring tasks error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: any) => {
    const response = await apiService.createRecurringTask(data);
    if (response.success) {
      loadTasks();
    } else {
      throw new Error(response.error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await apiService.deleteRecurringTask(id);
      if (response.success) {
        setTasks(tasks.filter(t => t.id !== id));
      }
    } catch (err) {
      setError('Failed to delete recurring task');
    }
    setDeleteConfirm(null);
  };

  const handleGenerateToday = async () => {
    try {
      setGenerating(true);
      const today = new Date().toISOString().split('T')[0];
      const response = await apiService.generateTasksForDate(today);
      if (response.success) {
        alert(`Generated ${response.tasks.length} tasks for today!`);
      }
    } catch (err) {
      setError('Failed to generate tasks');
    } finally {
      setGenerating(false);
    }
  };

  const getLabelColor = (label: string) => {
    switch (label) {
      case 'office': return 'primary';
      case 'personal': return 'secondary';
      case 'health': return 'success';
      case 'learning': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Repeat color="primary" />
          Recurring Tasks
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleGenerateToday}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} /> : <CalendarToday />}
            size="small"
          >
            Generate Today
          </Button>
          <Button
            variant="contained"
            onClick={() => setFormOpen(true)}
            startIcon={<Add />}
            size="small"
          >
            Add Recurring Task
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tasks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Repeat sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            No recurring tasks yet. Create one to automate your daily routines!
          </Typography>
        </Box>
      ) : (
        <List>
          {tasks.map((task) => (
            <ListItem
              key={task.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                bgcolor: 'background.paper'
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {task.title}
                    </Typography>
                    <Chip 
                      label={task.label} 
                      size="small" 
                      color={getLabelColor(task.label) as any}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule sx={{ fontSize: 16 }} />
                        {formatTime(task.scheduled_hour)}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Repeat sx={{ fontSize: 16 }} />
                        {task.patternDescription}
                      </Typography>

                      {task.reminder_enabled && (
                        <Tooltip title={`Reminder ${task.reminder_minutes_before} min before`}>
                          <Chip
                            icon={<NotificationsActive sx={{ fontSize: 14 }} />}
                            label={`${task.reminder_minutes_before}m`}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        </Tooltip>
                      )}
                    </Box>
                    
                    {task.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {task.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  onClick={() => setDeleteConfirm(task.id)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Create Form */}
      <RecurringTaskForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Recurring Task?</DialogTitle>
        <DialogContent>
          <Typography>
            This will stop generating new tasks from this pattern. Existing tasks won't be affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button 
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RecurringTasksList;
